"use client"
import { Card, Button, Badge, Row, Col, Form } from "react-bootstrap"
import { toast } from "react-hot-toast"
import { useState, useEffect } from "react"
import { apiRequest, endpoints } from "../utils/api"

const LocationSummary = ({ location, yearRange }) => {
  const [downloadFormat, setDownloadFormat] = useState("html")
  const [locationDetails, setLocationDetails] = useState({
    city: location.city || "Unknown City",
    country: location.country || "Unknown Country",
  })

  const fetchLocationDetails = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`,
      )
      const data = await response.json()
      setLocationDetails({
        city: data.city || data.locality || "Unknown City",
        country: data.countryName || "Unknown Country",
      })
    } catch (error) {
      console.error("Error fetching location details:", error)
    }
  }

  useEffect(() => {
    if (location.lat && location.lng && (!location.city || !location.country)) {
      fetchLocationDetails(location.lat, location.lng)
    }
  }, [location.lat, location.lng, location.city, location.country])

  const handleDownloadReport = async (format = "html") => {
    try {
      if (format === "pdf") {
        const response = await apiRequest(`${endpoints.reports}/pdf`, {
          method: "POST",
          body: JSON.stringify({
            location: {
              lat: location.lat,
              lng: location.lng,
              name: location.name,
            },
            crops: location.crops,
            weatherData: location.weatherData,
            period: { years: yearRange },
          }),
        })

        const blob = new Blob([response], { type: "application/pdf" })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = `agroplanner-report-${location.lat.toFixed(4)}-${location.lng.toFixed(4)}.pdf`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)

        toast.success("PDF report downloaded successfully!")
      } else {
        const response = await apiRequest(`${endpoints.reports}/json`, {
          method: "POST",
          body: JSON.stringify({
            location: {
              lat: location.lat,
              lng: location.lng,
              name: location.name,
            },
            crops: location.crops,
            weatherData: location.weatherData,
            period: { years: yearRange },
          }),
        })

        const htmlContent = generateModernReport(response)
        const blob = new Blob([htmlContent], { type: "text/html" })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = `agroplanner-report-${location.lat.toFixed(4)}-${location.lng.toFixed(4)}.html`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)

        toast.success("HTML report downloaded successfully!")
      }
    } catch (error) {
      console.error("Error downloading report:", error)
      toast.error("Failed to download report")
    }
  }

  const generateModernReport = (data) => {
    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${data.metadata.title}</title>
    <style>
        body { font-family: 'Segoe UI', sans-serif; margin: 0; padding: 20px; background: #f8f9fa; }
        .container { max-width: 800px; margin: 0 auto; background: white; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 6px rgba(0,0,0,0.1); }
        .header { background: linear-gradient(135deg, #28a745, #20c997); color: white; padding: 2rem; text-align: center; }
        .content { padding: 2rem; }
        .section { margin-bottom: 2rem; padding: 1.5rem; background: #f8f9fa; border-radius: 8px; border-left: 4px solid #28a745; }
        .crop-grid { display: grid; gap: 1rem; }
        .crop-card { background: white; border: 1px solid #e9ecef; border-radius: 8px; padding: 1rem; display: flex; align-items: center; gap: 1rem; }
        .badge { padding: 0.5rem 1rem; border-radius: 20px; font-weight: 600; }
        .high { background: #d4edda; color: #155724; }
        .medium { background: #fff3cd; color: #856404; }
        .low { background: #d1ecf1; color: #0c5460; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>🌱 ${data.metadata.title}</h1>
            <p>Generated on ${data.metadata.analysisDetails.generatedDate}</p>
        </div>
        <div class="content">
            <div class="section">
                <h2>📍 Location: ${data.metadata.location.name}</h2>
                <p>Coordinates: ${data.metadata.location.coordinates}</p>
                <p>Analysis Period: ${data.metadata.analysisDetails.period}</p>
            </div>
            <div class="section">
                <h2>🌾 Crop Recommendations (${data.cropRecommendations.length} total)</h2>
                <div class="crop-grid">
                    ${data.cropRecommendations
                      .map(
                        (crop, index) => `
                        <div class="crop-card">
                            <div style="font-weight: bold; color: #28a745;">#${index + 1}</div>
                            <div style="flex: 1;">
                                <h4 style="margin: 0 0 0.5rem 0;">${crop.name}</h4>
                                <small>Growth: ${crop.growth_period_days} days | Temp: ${crop.min_temp}°C - ${crop.max_temp}°C</small>
                            </div>
                            <div class="badge ${crop.suitabilityScore >= 80 ? "high" : crop.suitabilityScore >= 60 ? "medium" : "low"}">${crop.suitabilityScore}%</div>
                        </div>
                    `,
                      )
                      .join("")}
                </div>
            </div>
        </div>
    </div>
</body>
</html>`
  }

  return (
    <div className="sticky-top" style={{ top: "20px" }}>
      <Card className="shadow-sm mb-3">
        <Card.Header className="bg-primary text-white">
          <h6 className="mb-0">📍 Location Summary</h6>
        </Card.Header>
        <Card.Body>
          <h6 className="fw-bold">{location.name}</h6>
          <p className="text-muted small mb-1">
            📍 {locationDetails.city}, {locationDetails.country}
          </p>
          <p className="text-muted small mb-2">
            Coordinates: {location.lat.toFixed(4)}, {location.lng.toFixed(4)}
          </p>
          <p className="text-muted small mb-0">
            Analysis Period: {yearRange} year{yearRange > 1 ? "s" : ""} average
          </p>
        </Card.Body>
      </Card>

      <Card className="shadow-sm mb-3">
        <Card.Body>
          <Row className="text-center">
            <Col>
              <div className="display-6 fw-bold text-success">{location.suitableCrops}</div>
              <small className="text-muted">Suitable Crops</small>
            </Col>
            <Col>
              <div className="display-6 fw-bold text-info">{location.totalCrops}</div>
              <small className="text-muted">Total Analyzed</small>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {location.crops && location.crops.length > 0 && (
        <Card className="shadow-sm mb-3">
          <Card.Header className="bg-white">
            <h6 className="mb-0">🌾 Top Suitable Crops</h6>
          </Card.Header>
          <Card.Body>
            {location.crops.slice(0, 5).map((crop, index) => (
              <div key={index} className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-medium">{crop.name}</span>
                <Badge bg="success" className="rounded-pill">
                  {crop.suitabilityScore || 85}%
                </Badge>
              </div>
            ))}
            {location.crops.length > 5 && (
              <small className="text-muted">+{location.crops.length - 5} more crops below</small>
            )}
          </Card.Body>
        </Card>
      )}

      <Card className="shadow-sm">
        <Card.Header className="bg-white">
          <h6 className="mb-0">📄 Download Report</h6>
        </Card.Header>
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label className="small">Choose Format:</Form.Label>
            <div>
              <Form.Check
                inline
                type="radio"
                name="downloadFormat"
                id="format-html"
                label="Modern HTML"
                checked={downloadFormat === "html"}
                onChange={() => setDownloadFormat("html")}
              />
              <Form.Check
                inline
                type="radio"
                name="downloadFormat"
                id="format-pdf"
                label="PDF Document"
                checked={downloadFormat === "pdf"}
                onChange={() => setDownloadFormat("pdf")}
              />
            </div>
          </Form.Group>

          <Button
            variant="success"
            size="lg"
            className="w-100 mb-2"
            onClick={() => handleDownloadReport(downloadFormat)}
          >
            📄 Download {downloadFormat.toUpperCase()} Report
          </Button>

          <small className="text-muted d-block text-center">
            {downloadFormat === "html" ? "Interactive HTML with modern styling" : "Professional PDF document format"}
          </small>
        </Card.Body>
      </Card>
    </div>
  )
}

export default LocationSummary
